/**
 * GROUP SERVICE - Gestión de Grupos
 * Servicio para operaciones CRUD de grupos de agencias
 */

import { ApiClient } from './api-client';
import { fetchAllGroupPages, sharedClientGroupPages } from '../groupCatalogue';

export interface Group {
  id?: number;
  name: string;
  description?: string;
  supervisorId?: number | null;
  region_code: string;
  provincia_code: string;
  fondo_asignado?: number;
  gastos_registrados?: number;
  member_count?: number;
  is_active?: boolean;
  // P13-V7B: `GET /api/groups` publishes the timestamps as `createdAt` / `updatedAt`
  // (groupController.list). The snake_case names are kept for a payload that still carries them.
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  last_activity_at?: string;
  supervisor?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

/**
 * P13-V7B — what `POST /api/groups` requires (`groupSchemas.create`, `groupController.create`).
 *
 * `targetAdministrationId` is the ONLY administration field the writer accepts, and it is mandatory:
 * the vendor names the administration the group belongs to and the server validates that it exists.
 * The aliases `administration_id` / `administrationId` are refused outright
 * (`ADMINISTRATION_PAYLOAD_NOT_AUTHORITY`). `description` is mandatory and may not be empty. The form
 * used to send neither, so every creation answered 400.
 */
export interface CreateGroupData {
  name: string;
  description: string;
  supervisorId?: number | null;
  region_code: string;
  provincia_code: string;
  fondo_asignado?: number;
  is_active?: boolean;
  targetAdministrationId: number | null;
}

export interface UpdateGroupData {
  name?: string;
  description?: string;
  supervisorId?: number | null;
  region_code?: string;
  provincia_code?: string;
  fondo_asignado?: number;
  is_active?: boolean;
}

/** The fields `groupSchemas.update` accepts. No administration field is among them. */
export const GROUP_UPDATE_FIELDS = Object.freeze([
  'name',
  'description',
  'supervisorId',
  'region_code',
  'provincia_code',
  'fondo_asignado',
  'is_active'
] as const);

export class GroupService {
  static async getAll(): Promise<Group[]> {
    try {
      // P13-PROD-SCALE-ORG-REPORTING-BOUNDED-READS-WS1: GET /groups answers one bounded page. The management table counts and
      // filters client-side, so it needs the WHOLE directory: every page is read, or the read fails.
      const groups = await fetchAllGroupPages<Group>(sharedClientGroupPages(ApiClient));
      console.log('📊 GroupService.getAll: directorio completo de', groups.length, 'grupos');
      return groups;
    } catch (error) {
      // Sanitized: avoid logging the raw Axios error (carries token/headers/payload).
      console.error('Group list request failed');
      throw error;
    }
  }

  static async getById(id: number): Promise<Group> {
    try {
      const response = await ApiClient.get<Group>(`/groups/${id}`);
      return response.data;
    } catch (error) {
      // Sanitized: avoid logging the raw Axios error (carries token/headers/payload).
      console.error('Group fetch request failed');
      throw error;
    }
  }

  static async create(groupData: CreateGroupData): Promise<Group> {
    try {
      console.log('🚀 GroupService.create: Creando grupo...');
      // P13-V7B: the payload is built field by field. Spreading the form would forward whatever the
      // form holds, and a stray administration alias makes the writer refuse the whole request.
      const payload: Record<string, unknown> = {
        name: groupData.name,
        description: groupData.description,
        region_code: groupData.region_code,
        provincia_code: groupData.provincia_code,
        targetAdministrationId: groupData.targetAdministrationId
      };
      if (groupData.supervisorId !== undefined && groupData.supervisorId !== null) {
        payload.supervisorId = groupData.supervisorId;
      }
      if (groupData.fondo_asignado !== undefined && groupData.fondo_asignado !== null) {
        payload.fondo_asignado = groupData.fondo_asignado;
      }
      if (typeof groupData.is_active === 'boolean') payload.is_active = groupData.is_active;
      const response = await ApiClient.post<Group>('/groups', payload);
      console.log('✅ GroupService.create: Grupo creado exitosamente');
      return response.data;
    } catch (error) {
      // Sanitized: avoid logging the raw Axios error (carries token/headers/payload).
      console.error('Group create request failed');
      throw error;
    }
  }

  static async update(id: number, groupData: UpdateGroupData): Promise<Group> {
    try {
      // P13-V7B: a group's administration is immutable. `PUT /api/groups/:id` refuses the whole
      // request when ANY administration field is present (`TARGET_ADMINISTRATION_NOT_ALLOWED` /
      // `ADMINISTRATION_PAYLOAD_NOT_AUTHORITY`), so only the fields the update schema names travel.
      const payload: Record<string, unknown> = {};
      for (const field of GROUP_UPDATE_FIELDS) {
        const value = (groupData as Record<string, unknown>)[field];
        if (value !== undefined) payload[field] = value;
      }
      const response = await ApiClient.put<Group>(`/groups/${id}`, payload);
      return response.data;
    } catch (error) {
      // Sanitized: avoid logging the raw Axios error (carries token/headers/payload).
      console.error('Group update request failed');
      throw error;
    }
  }

  static async delete(id: number): Promise<void> {
    try {
      await ApiClient.delete(`/groups/${id}`);
    } catch (error) {
      // Sanitized: avoid logging the raw Axios error (carries token/headers/payload).
      console.error('Group delete request failed');
      throw error;
    }
  }
}

export default GroupService;
